/**
 * Financing calculation engine.
 *
 * Rate logic mirrors the legacy Lender#get_roi model:
 *   finalRate = lender.roi + matchingBand.variable_interest_rate
 *
 * PMT formula is the standard annuity formula used by Exonio / Excel.
 */

// Available amortization periods (months)
export const amortizationPeriods = [
  { months: 36, label: '36 months (3 yr)' },
  { months: 48, label: '48 months (4 yr)' },
  { months: 60, label: '60 months (5 yr)' },
  { months: 72, label: '72 months (6 yr)' },
  { months: 84, label: '84 months (7 yr)' },
  { months: 96, label: '96 months (8 yr)' },
  { months: 108, label: '108 months (9 yr)' },
  { months: 120, label: '120 months (10 yr)' },
];

/**
 * Determine the interest rate for a given lender and loan amount.
 * Matches legacy: base roi + matching band's variable_interest_rate.
 */
export function getInterestRate(lender, loanAmount) {
  const baseRate = lender.roi || 0;
  const bands = lender.rateBands || lender.rate_bands || [];

  const matchingBand = bands.find(
    (band) =>
      loanAmount >= (band.loanAmountLowerBand || band.loan_amount_lower_band) &&
      loanAmount <= (band.loanAmountUpperBand || band.loan_amount_upper_band)
  );

  const variableRate = matchingBand
    ? matchingBand.variableInterestRate || matchingBand.variable_interest_rate || 0
    : 0;

  return baseRate + variableRate;
}

/**
 * Calculate monthly payment (EMI) using the standard annuity formula.
 * annualRate is a decimal (e.g. 0.065 for 6.5%).
 */
export function calculateMonthlyPayment(principal, annualRate, months) {
  const monthlyRate = annualRate / 12;
  if (monthlyRate === 0) return principal / months;

  return (
    (principal * monthlyRate * Math.pow(1 + monthlyRate, months)) /
    (Math.pow(1 + monthlyRate, months) - 1)
  );
}

/**
 * Generate a complete month-by-month amortization schedule.
 */
export function calculateAmortizationSchedule(principal, annualRate, months) {
  const monthlyRate = annualRate / 12;
  const monthlyPayment = calculateMonthlyPayment(principal, annualRate, months);

  const schedule = [];
  let remainingBalance = principal;

  for (let month = 1; month <= months; month++) {
    const interestPayment = remainingBalance * monthlyRate;
    const principalPayment = monthlyPayment - interestPayment;
    remainingBalance -= principalPayment;

    if (month === months) remainingBalance = 0;

    schedule.push({
      month,
      year: Math.ceil(month / 12),
      payment: monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      remainingBalance: Math.max(0, remainingBalance),
    });
  }

  return schedule;
}

/**
 * Aggregate a monthly schedule into yearly summaries with nested monthly detail.
 */
export function groupByYear(monthlySchedule, originalPrincipal) {
  const yearlyMap = new Map();

  monthlySchedule.forEach((payment) => {
    if (!yearlyMap.has(payment.year)) {
      yearlyMap.set(payment.year, []);
    }
    yearlyMap.get(payment.year).push(payment);
  });

  const yearlyPayments = [];

  yearlyMap.forEach((months, year) => {
    const totalPayment = months.reduce((sum, m) => sum + m.payment, 0);
    const totalPrincipal = months.reduce((sum, m) => sum + m.principal, 0);
    const totalInterest = months.reduce((sum, m) => sum + m.interest, 0);
    const remainingBalance = months[months.length - 1].remainingBalance;
    const percentRemaining = originalPrincipal > 0
      ? (remainingBalance / originalPrincipal) * 100
      : 0;

    yearlyPayments.push({
      year,
      totalPayment,
      totalPrincipal,
      totalInterest,
      remainingBalance,
      percentRemaining,
      monthlyBreakdown: months,
    });
  });

  return yearlyPayments;
}

/**
 * Main calculation orchestrator.
 * Returns monthly payment, total interest, total cost, effective rate, and yearly schedule.
 */
export function calculateFinancing(lender, loanAmount, amortizationMonths) {
  const interestRate = getInterestRate(lender, loanAmount);
  const monthlyPayment = calculateMonthlyPayment(loanAmount, interestRate, amortizationMonths);
  const monthlySchedule = calculateAmortizationSchedule(loanAmount, interestRate, amortizationMonths);
  const yearlySchedule = groupByYear(monthlySchedule, loanAmount);

  const totalInterest = monthlySchedule.reduce((sum, m) => sum + m.interest, 0);
  const totalCost = loanAmount + totalInterest;

  return {
    monthlyPayment,
    totalInterest,
    totalCost,
    interestRate,
    schedule: yearlySchedule,
  };
}

// ---------------------------------------------------------------------------
// Formatting utilities
// ---------------------------------------------------------------------------

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatCurrencyDetailed(amount) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatPercentage(value, decimals = 2) {
  return `${(value * 100).toFixed(decimals)}%`;
}

export function getLenderRateRange(lender) {
  const bands = lender.rateBands || lender.rate_bands || [];
  if (bands.length === 0) return formatPercentage(lender.roi || 0);

  const baseRate = lender.roi || 0;
  const variableRates = bands.map(
    (b) => b.variableInterestRate || b.variable_interest_rate || 0
  );

  const minTotal = baseRate + Math.min(...variableRates);
  const maxTotal = baseRate + Math.max(...variableRates);

  if (minTotal === maxTotal) return formatPercentage(minTotal);
  return `${formatPercentage(minTotal)} - ${formatPercentage(maxTotal)}`;
}

/**
 * Derive dynamic amortization periods from a lender's min/max settings,
 * falling back to the static list if no lender data.
 */
export function getLenderAmortizationPeriods(lender) {
  if (!lender) return amortizationPeriods;

  const min = lender.loanAmortizationMinimum || lender.loan_amortization_minimum || 36;
  const max = lender.loanAmortizationMaximum || lender.loan_amortization_maximum || 120;

  return amortizationPeriods.filter((p) => p.months >= min && p.months <= max);
}

/**
 * Get the min/max loan amounts for a lender, preferring schedule-derived values.
 */
export function getLenderLoanRange(lender) {
  return {
    min: lender.minimumLoanAmount || lender.minimum_loan_amount || lender.loanAmountMinimum || lender.loan_amount_minimum || 50000,
    max: lender.maximumLoanAmount || lender.maximum_loan_amount || lender.loanAmountMaximum || lender.loan_amount_maximum || 10000000,
  };
}
